import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShoppingCart, X, Bell } from 'lucide-react';
import { toast } from 'sonner';
import AltModal from './AltModal';
import BuyLaterButton from './BuyLaterButton';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

const formatBadgePrice = (price) =>
  Math.round(price || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

function getPackingRate(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

function normalizeQuantityWithPacking(quantity, packingRate) {
  const parsedQty = parseInt(quantity, 10);
  const safeQty = Number.isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
  return Math.max(safeQty, getPackingRate(packingRate));
}

export default function BulkMatchModal({
  open,
  onClose,
  cartProductQtyMap,
  openOrderProductMap,
  catalogFilterBody,
  onAddToCart,
  onCartRefresh,
  user,
  token,
}) {
  const [bulkInput, setBulkInput] = useState('');
  const [bulkMatches, setBulkMatches] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Internal alt modal state
  const [altModalOpen, setAltModalOpen] = useState(false);
  const [altOptions, setAltOptions] = useState([]);
  const [altTargetIndex, setAltTargetIndex] = useState(null);
  const [altCurrentId, setAltCurrentId] = useState(null);

  const parseBulkQuantity = (raw) => {
    if (!raw || !raw.trim()) return { quantity: 1, isReturn: false };
    const trimmed = raw.trim();
    if (trimmed.startsWith('-')) {
      const num = parseFloat(trimmed.replace(/\s/g, '').replace(/,/g, '.'));
      if (!Number.isNaN(num) && num < 0) {
        return { isReturn: true };
      }
    }
    const qtyVal = parseInt(trimmed, 10);
    const quantity = Number.isNaN(qtyVal) || qtyVal <= 0 ? 1 : qtyVal;
    return { quantity, isReturn: false };
  };

  const parseBulkInput = () => {
    const lines = bulkInput.split('\n').map((line) => line.trim()).filter(Boolean);
    let skippedReturns = 0;
    const items = lines.map((line) => {
      const parts = line.split(/\t+/);
      const name = parts[0] ? parts[0].trim() : '';
      const articul = parts[1] ? parts[1].trim() : '';
      const qtyParsed = parseBulkQuantity(parts[2]);
      if (qtyParsed.isReturn) {
        skippedReturns += 1;
        return null;
      }
      return { name, articul, quantity: qtyParsed.quantity };
    }).filter((item) => item && (item.name || item.articul));
    return { items, skippedReturns };
  };

  const fetchBulkMatches = async () => {
    if (!user || !token) {
      toast.error('Войдите в систему для массового заказа');
      return;
    }
    const { items, skippedReturns } = parseBulkInput();
    if (!items.length) {
      if (skippedReturns > 0) {
        toast.info(`Пропущено ${skippedReturns} строк с отрицательным количеством (возвраты)`);
      } else {
        toast.error('Добавьте строки с наименованием и артикулом');
      }
      return;
    }
    if (skippedReturns > 0) {
      toast.info(`Пропущено ${skippedReturns} строк с отрицательным количеством (возвраты)`);
    }
    setBulkLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/products/bulk-match`,
        { items, ...(catalogFilterBody || {}) },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      setBulkMatches(
        (data?.results || []).map((row, idx) => {
          let suggestion = row.suggestion || null;
          let alternatives = row.alternatives || [];
          let isSubstituted = false;

          if (suggestion && !(suggestion.in_stock > 0) && alternatives.length > 0) {
            const bestAlt = [...alternatives]
              .filter((a) => a.in_stock > 0)
              .sort((a, b) => (a.client_price || 0) - (b.client_price || 0))[0];
            if (bestAlt) {
              alternatives = [suggestion, ...alternatives.filter((a) => a.id !== bestAlt.id)];
              suggestion = bestAlt;
              isSubstituted = true;
            }
          }

          return {
            ...row,
            id: `${row.input_articul || row.input_name || 'row'}-${idx}`,
            suggestion,
            alternatives,
            isSubstituted,
            quantity: normalizeQuantityWithPacking(row.quantity, suggestion?.packing_rate),
          };
        })
      );
    } catch (error) {
      const timedOut = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      toast.error(
        timedOut
          ? 'Подбор занимает слишком много времени. Попробуйте меньший список или повторите позже.'
          : (error.response?.data?.detail || 'Не удалось подобрать товары')
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const openAlternativesModal = (rowIndex) => {
    const target = bulkMatches[rowIndex];
    const opts = [
      ...(target?.suggestion ? [target.suggestion] : []),
      ...(target?.alternatives || []),
    ];
    const unique = [];
    const seen = new Set();
    for (const p of opts) {
      if (p?.id && !seen.has(p.id)) { seen.add(p.id); unique.push(p); }
    }
    unique.sort((a, b) => {
      const aS = (a.in_stock || 0) > 0 ? 1 : 0;
      const bS = (b.in_stock || 0) > 0 ? 1 : 0;
      if (bS !== aS) return bS - aS;
      return (a.client_price || 0) - (b.client_price || 0);
    });
    setAltOptions(unique);
    setAltCurrentId(target?.suggestion?.id || null);
    setAltTargetIndex(rowIndex);
    setAltModalOpen(true);
  };

  const selectAlternative = (product) => {
    if (altTargetIndex === null) return;
    setBulkMatches((prev) =>
      prev.map((row, idx) => {
        if (idx !== altTargetIndex) return row;
        const packingRate = getPackingRate(product?.packing_rate);
        const prevSuggestion = row.suggestion;
        const newAlts = [
          ...(prevSuggestion && prevSuggestion.id !== product.id ? [prevSuggestion] : []),
          ...(row.alternatives || []).filter((a) => a.id !== product.id),
        ];
        return {
          ...row,
          suggestion: product,
          alternatives: newAlts,
          isSubstituted: false,
          quantity: normalizeQuantityWithPacking(row.quantity, packingRate),
        };
      })
    );
    setAltCurrentId(product.id);
    setAltModalOpen(false);
  };

  const addBulkToCart = async () => {
    if (!user || !token) {
      toast.error('Войдите в систему для добавления в корзину');
      return;
    }
    const ready = bulkMatches.filter((row) => row.suggestion && row.suggestion.id);
    if (!ready.length) { toast.error('Нет подобранных товаров для добавления'); return; }

    setBulkLoading(true);
    try {
      const items = ready.map((row) => ({
        product_id: row.suggestion.id,
        quantity: normalizeQuantityWithPacking(row.quantity, row.suggestion?.packing_rate),
      }));
      const { data } = await axios.post(
        `${API}/cart/items/batch`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const s = data.success ?? 0;
      const f = data.failed ?? 0;
      if (s > 0) {
        toast.success(`Добавлено в корзину: ${s} позиций${f ? `, ошибок: ${f}` : ''}`);
        onCartRefresh?.();
      } else {
        toast.error('Не удалось добавить товары в корзину');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка добавления в корзину');
    } finally {
      setBulkLoading(false);
    }
  };

  const notifyWhenAvailable = async (product, rowIndex) => {
    if (!user || !token) { toast.error('Войдите, чтобы получать уведомления'); return; }
    try {
      await axios.patch(
        `${API}/products/${product.id}/wish-list`,
        { wish_list: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Добавлено в ожидаемые');
      setBulkMatches((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex
            ? { ...row, suggestion: row.suggestion?.id === product.id ? { ...row.suggestion, wish_list: true } : row.suggestion }
            : row
        )
      );
    } catch { toast.error('Не удалось добавить в ожидаемые'); }
  };

  const toggleAltWishList = async (product) => {
    if (!user || !token) { toast.error('Войдите, чтобы получать уведомления'); return; }
    const newVal = !product.wish_list;
    try {
      await axios.patch(
        `${API}/products/${product.id}/wish-list`,
        { wish_list: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAltOptions((prev) => prev.map((p) => p.id === product.id ? { ...p, wish_list: newVal } : p));
      toast.success(newVal ? 'Добавлено в ожидаемые' : 'Убрано из ожидаемых');
    } catch { toast.error('Не удалось обновить список ожидания'); }
  };

  const updateRowSuggestion = (rowIndex, productId, patch) => {
    setBulkMatches((prev) =>
      prev.map((row, idx) =>
        idx === rowIndex && row.suggestion?.id === productId
          ? { ...row, suggestion: { ...row.suggestion, ...patch } }
          : row
      )
    );
  };

  const setBuyLater = async (productId, days, rowIndex) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы добавить в отлежку');
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API}/products/${productId}/buy-later`,
        { buy_later: true, days },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateRowSuggestion(rowIndex, productId, {
        buy_later: true,
        buy_later_at: data.buy_later_at,
        buy_later_notified: false,
      });
      toast.success(`Напомним купить через ${days} д.`);
    } catch {
      toast.error('Не удалось добавить в отлежку');
    }
  };

  const removeBuyLater = async (productId, rowIndex) => {
    if (!user || !token) {
      toast.error('Войдите, чтобы управлять отлежкой');
      return;
    }
    try {
      await axios.patch(
        `${API}/products/${productId}/buy-later`,
        { buy_later: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateRowSuggestion(rowIndex, productId, {
        buy_later: false,
        buy_later_at: null,
        buy_later_notified: false,
      });
      toast.success('Убрано из отлежки');
    } catch {
      toast.error('Не удалось убрать из отлежки');
    }
  };

  const foundCount = bulkMatches.filter((r) => r.suggestion).length;
  const notFoundCount = bulkMatches.filter((r) => !r.suggestion).length;
  const inCartCount = bulkMatches.filter((r) => r.suggestion && cartProductQtyMap.has(r.suggestion.id)).length;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-[1180px] max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-gray-900">Массовый заказ</DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <div className="flex items-center gap-0">
                <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-l border border-indigo-200 font-medium">Наименование</span>
                <span className="bg-gray-50 text-gray-400 px-1.5 py-1 border-y border-gray-200 text-[10px] font-mono">TAB</span>
                <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 border border-indigo-200 font-medium">Артикул</span>
                <span className="bg-gray-50 text-gray-400 px-1.5 py-1 border-y border-gray-200 text-[10px] font-mono">TAB</span>
                <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-r border border-indigo-200 font-medium">Кол-во</span>
              </div>
              <span className="text-gray-400">· Данные из Excel вставляются напрямую</span>
            </div>
          </div>

          {/* Input */}
          <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <textarea
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              className="w-full h-28 border border-gray-200 rounded-lg p-3 text-sm font-mono resize-none bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              placeholder={"Амортизатор задний Vesta Truckman\t8450006786\t2\nШтанга реактивная 2101\t21010-2917400\t1"}
            />
            <div className="flex items-center justify-end gap-2 mt-2.5">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-red-500"
                onClick={() => { setBulkInput(''); setBulkMatches([]); }}
              >
                <X className="w-3.5 h-3.5 mr-1" />Очистить
              </Button>
              <Button size="sm" onClick={fetchBulkMatches} disabled={bulkLoading} className="min-w-[100px]">
                {bulkLoading ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    Ищем…
                  </span>
                ) : 'Подобрать'}
              </Button>
            </div>
          </div>

          {/* Results */}
          {bulkMatches.length > 0 && (
            <>
              <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0 flex items-center gap-5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="text-sm font-bold text-emerald-700">{foundCount}</span>
                  <span className="text-xs text-gray-500">найдено</span>
                </div>
                {notFoundCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="text-sm font-bold text-red-500">{notFoundCount}</span>
                    <span className="text-xs text-gray-500">не найдено</span>
                  </div>
                )}
                {inCartCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                    <span className="text-sm font-bold text-blue-600">{inCartCount}</span>
                    <span className="text-xs text-gray-500">в корзине</span>
                  </div>
                )}
                <div className="ml-auto">
                  <Button
                    size="sm"
                    onClick={addBulkToCart}
                    disabled={bulkLoading || foundCount === 0}
                    className="gap-1.5"
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                    Добавить найденные ({foundCount})
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 pt-3 pb-5 min-h-0">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="pb-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Запрос</th>
                      <th className="pb-2 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-32">Кол-во</th>
                      <th className="pb-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Подобранный товар</th>
                      <th className="pb-2 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-52">Цена / Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkMatches.map((row, idx) => {
                      const packingRate = getPackingRate(row.suggestion?.packing_rate);
                      const qty = normalizeQuantityWithPacking(row.quantity, packingRate);
                      const isInCart = row.suggestion && cartProductQtyMap.has(row.suggestion.id);
                      const cartQty = row.suggestion ? cartProductQtyMap.get(row.suggestion.id) : null;
                      const isNotFound = !row.suggestion;
                      const inOrder = row.suggestion && openOrderProductMap.has(row.suggestion.id);
                      const openOrderInfo = row.suggestion ? openOrderProductMap.get(row.suggestion.id) : null;
                      const inTransit = row.suggestion?.in_transit === true;
                      const outOfStock = row.suggestion && !(row.suggestion.in_stock > 0) && !inTransit;
                      const hasBetterAlt = !!row.suggestion?.has_cheaper_alt;
                      const cheaperAltPrice = row.suggestion?.cheaper_alt_price ?? null;
                      const inWishList = row.suggestion?.wish_list === true;

                      return (
                        <tr
                          key={row.id}
                          className={`border-b border-gray-50 transition-colors ${
                            isNotFound ? 'opacity-55' : isInCart ? 'bg-blue-50/30 hover:bg-blue-50/50' : 'hover:bg-gray-50/70'
                          }`}
                        >
                          <td className="py-3 pr-4 align-top">
                            <p className="font-medium text-gray-900 text-[13px] leading-snug">{row.input_name || '—'}</p>
                            {row.input_articul && (
                              <p className="font-mono text-[11px] text-gray-400 mt-0.5">{row.input_articul}</p>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center align-middle">
                            {!isNotFound && (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  className="w-6 h-6 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300 flex items-center justify-center text-base leading-none flex-shrink-0 transition-colors"
                                  onClick={() => {
                                    const next = normalizeQuantityWithPacking(qty - packingRate, packingRate);
                                    setBulkMatches((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: next } : r));
                                  }}
                                >−</button>
                                <input
                                  type="number"
                                  min={packingRate}
                                  step={packingRate}
                                  value={qty}
                                  className="w-12 h-6 text-center text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                                  onChange={(e) => {
                                    const val = normalizeQuantityWithPacking(e.target.value, packingRate);
                                    setBulkMatches((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: val } : r));
                                  }}
                                />
                                <button
                                  className="w-6 h-6 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300 flex items-center justify-center text-base leading-none flex-shrink-0 transition-colors"
                                  onClick={() => {
                                    const next = normalizeQuantityWithPacking(qty + packingRate, packingRate);
                                    setBulkMatches((prev) => prev.map((r, i) => i === idx ? { ...r, quantity: next } : r));
                                  }}
                                >+</button>
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-4 align-top">
                            {row.suggestion ? (
                              <div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-medium text-gray-900 text-[13px] leading-snug">{row.suggestion.name}</span>
                                  {row.isSubstituted && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full border border-orange-200">Замена</span>
                                  )}
                                  {hasBetterAlt && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full border border-violet-200">
                                      {cheaperAltPrice != null
                                        ? `Есть дешевле за ${formatBadgePrice(cheaperAltPrice)}`
                                        : 'Есть дешевле'}
                                    </span>
                                  )}
                                  {isInCart && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">
                                      <ShoppingCart className="w-2.5 h-2.5" />
                                      В корзине{cartQty ? ` × ${cartQty}` : ''}
                                    </span>
                                  )}
                                  {inOrder && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full">
                                      В заказе{openOrderInfo?.quantity ? ` × ${openOrderInfo.quantity}` : ''}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2.5 mt-0.5">
                                  {row.suggestion.brand && <span className="text-[11px] text-gray-500">{row.suggestion.brand}</span>}
                                  <span className={`text-[11px] font-medium ${
                                    row.suggestion.in_stock > 0
                                      ? 'text-emerald-600'
                                      : inTransit
                                        ? 'text-blue-600'
                                        : 'text-gray-400'
                                  }`}>
                                    {row.suggestion.in_stock > 0
                                      ? `В наличии: ${row.suggestion.in_stock}`
                                      : inTransit
                                        ? 'В пути на склад'
                                        : 'Нет в наличии'}
                                  </span>
                                  {packingRate > 1 && (
                                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">×{packingRate}</span>
                                  )}
                                  <button
                                    className={`transition-colors ${inWishList ? 'text-amber-400' : 'text-gray-300 hover:text-amber-400'}`}
                                    onClick={() => notifyWhenAvailable(row.suggestion, idx)}
                                    title={inWishList ? 'В листе ожидания' : 'Уведомить о наличии'}
                                  >
                                    <Bell className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-[12px] text-red-400 font-medium">Ничего не найдено</span>
                            )}
                          </td>
                          <td className="py-3 pl-4 align-middle">
                            <div className="flex items-center justify-end gap-3">
                              <div className="text-right min-w-[64px]">
                                {row.suggestion ? (
                                  <span className="font-bold text-gray-900 text-sm tabular-nums">
                                    {row.suggestion.client_price?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-sm">—</span>
                                )}
                              </div>
                              <div className="w-px h-6 bg-gray-200 flex-shrink-0" />
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-3 text-xs font-medium border-gray-300 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                  onClick={() => openAlternativesModal(idx)}
                                  disabled={!row.suggestion && !row.alternatives?.length}
                                >
                                  Выбрать
                                </Button>
                                {row.suggestion && (
                                  <>
                                    <BuyLaterButton
                                      product={row.suggestion}
                                      disabled={!user}
                                      onSetDays={(days) => setBuyLater(row.suggestion.id, days, idx)}
                                      onRemove={() => removeBuyLater(row.suggestion.id, idx)}
                                      className={`h-7 w-7 p-0 flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 ${
                                        row.suggestion.buy_later ? 'bg-orange-50 text-orange-600 border-orange-200' : 'text-gray-500 hover:text-orange-600'
                                      }`}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-7 p-0 transition-colors disabled:opacity-30 ${
                                        isInCart ? 'text-blue-500 hover:text-blue-700' : 'text-gray-400 hover:text-blue-600'
                                      }`}
                                      disabled={outOfStock}
                                      title={outOfStock ? 'Нет в наличии' : inTransit ? 'В пути — можно заказать' : isInCart ? 'Уже в корзине' : 'Добавить в корзину'}
                                      onClick={() => onAddToCart(row.suggestion.id, qty)}
                                    >
                                      <ShoppingCart className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Internal alt modal (separate from main product alt modal) */}
      <AltModal
        open={altModalOpen}
        onClose={() => setAltModalOpen(false)}
        options={altOptions}
        currentId={altCurrentId}
        mode="bulk"
        loading={false}
        onSelect={selectAlternative}
        onToggleWishList={toggleAltWishList}
      />
    </>
  );
}
